<template>
  <RouterLink :to="pageUrl" class="block group relative overflow-hidden">
    <Transition name="page-card">
      <!-- Active card: solid #153838 -->
      <div
        v-if="isActive"
        key="active"
        class="z-10 flex flex-col bg-[#153838] w-full h-full pt-5 pr-5 pb-5 pl-5 relative justify-between rounded-2xl min-h-[200px]"
      >
      <div class="flex justify-between items-start">
        <span class="text-[10px] font-semibold text-[#ecd79f] uppercase tracking-widest bg-white/5 px-2.5 py-1 rounded-lg border border-white/10 backdrop-blur-md shadow-sm">
          {{ pageLabel }}
        </span>
        <div class="w-6 h-6 rounded-full bg-[#ecd79f] text-[#153838] flex items-center justify-center shadow-lg shrink-0">
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-check w-3.5 h-3.5"><path d="M20 6 9 17l-5-5"></path></svg>
        </div>
      </div>

      <div class="flex flex-col gap-3 mt-1 flex-1 min-h-0">
        <h3 class="font-serif text-xl font-medium text-white leading-tight tracking-tight">
          {{ pageTitle }}
        </h3>
        <p class="text-[11px] text-white/70 line-clamp-2 leading-relaxed font-medium">
          {{ excerpt }}
        </p>

        <div class="relative w-full h-24 rounded-lg overflow-hidden border border-white/10 shadow-sm mt-1 shrink-0">
          <img
            v-if="imageUrl"
            :src="imageUrl"
            :alt="pageTitle"
            class="w-full h-full object-cover opacity-90 hover:opacity-100 transition-opacity duration-300"
          >
          <div v-else class="w-full h-full bg-white/5 flex items-center justify-center">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-8 h-8 text-white/30"><rect width="18" height="18" x="3" y="3" rx="2" ry="2"></rect><circle cx="9" cy="9" r="2"></circle><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"></path></svg>
          </div>
        </div>
      </div>

      <div class="flex items-center justify-between border-t border-white/10 pt-3">
        <div class="flex items-center gap-4">
          <span v-if="readingTime" class="flex items-center gap-1.5 text-[10px] font-semibold text-[#ecd79f]">
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-clock w-3 h-3"><path d="M12 6v6l4 2"></path><circle cx="12" cy="12" r="10"></circle></svg>
            {{ readingTime }}
          </span>
          <span v-if="viewCount != null" class="flex items-center gap-1.5 text-[10px] font-medium text-white/50">
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-eye w-3 h-3"><path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0"></path><circle cx="12" cy="12" r="3"></circle></svg>
            {{ viewCountFormatted }}
          </span>
        </div>
        <div class="flex items-center gap-1">
          <button type="button" class="p-1.5 text-white/60 hover:text-white hover:bg-white/10 rounded-lg transition-colors" title="Edit page" @click.stop.prevent="$emit('edit', page)">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z"/></svg>
          </button>
          <button type="button" class="p-1.5 text-white/60 hover:text-red-300 hover:bg-white/10 rounded-lg transition-colors" title="Delete page" @click.stop.prevent="$emit('delete', page)">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg>
          </button>
          <div class="text-white/80 hover:text-white transition-colors duration-300">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-arrow-right w-4 h-4"><path d="M5 12h14"></path><path d="m12 5 7 7-7 7"></path></svg>
          </div>
        </div>
      </div>
      </div>

      <!-- Inactive card: horizontal, white -->
      <div
        v-else
        key="inactive"
        class="group border border-black/[0.03] hover:border-[#1c4848]/20 hover:shadow-md transition-all duration-300 cursor-pointer flex gap-3.5 bg-white rounded-xl p-2.5 shadow-sm items-center"
    >
      <div class="relative w-14 h-[72px] shrink-0 rounded-lg overflow-hidden border border-gray-100 shadow-inner bg-gray-50">
        <img
          v-if="imageUrl"
          :src="imageUrl"
          :alt="pageTitle"
          class="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500 grayscale group-hover:grayscale-0"
        >
        <div v-else class="w-full h-full flex items-center justify-center bg-gray-100">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="text-gray-300"><rect width="18" height="18" x="3" y="3" rx="2" ry="2"></rect><circle cx="9" cy="9" r="2"></circle><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"></path></svg>
        </div>
      </div>
      <div class="flex-1 min-w-0 flex flex-col justify-center h-full">
        <div class="flex justify-between items-center mb-1">
          <span class="text-[9px] font-bold text-[#1c4848]/40 uppercase tracking-widest group-hover:text-[#1c4848]/60 transition-colors">{{ pageLabel }}</span>
          <div class="w-1.5 h-1.5 rounded-full shrink-0" :class="statusDotClass"></div>
        </div>
        <h3 class="font-serif text-[13px] text-[#1c4848] font-semibold leading-tight mb-1 truncate group-hover:text-[#153838]">
          {{ pageTitle }}
        </h3>
        <p class="text-[10px] text-[#1c4848]/40 line-clamp-1 leading-relaxed">
          {{ excerpt }}
        </p>
      </div>
      <div class="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" @click.stop>
        <button type="button" class="p-1.5 text-[#1c4848]/70 hover:bg-[#1c4848]/5 rounded-md transition-colors" title="Edit page" @click.stop.prevent="$emit('edit', page)">
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z"/></svg>
        </button>
        <button type="button" class="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded-md transition-colors" title="Publish to feed" @click.stop.prevent="$emit('publish', page)">
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12 19V5"/><path d="m5 12 7-7 7 7"/></svg>
        </button>
        <button type="button" class="p-1.5 text-red-500 hover:bg-red-50 rounded-md transition-colors" title="Delete page" @click.stop.prevent="$emit('delete', page)">
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg>
        </button>
      </div>
      </div>
    </Transition>
  </RouterLink>
</template>

<script setup>
import { computed } from 'vue';

defineEmits(['publish', 'delete', 'edit']);

const props = defineProps({
  page: { type: Object, required: true },
  index: { type: Number, required: true },
  bookId: { type: [String, Number], required: true },
  isActive: { type: Boolean, default: false }
});

const pageUrl = computed(() => `/books/${props.bookId}/pages/${props.page.id}`);
const pageLabel = computed(() => `Page ${String(props.index).padStart(2, '0')}`);
const pageTitle = computed(() => props.page?.title ?? props.page?.name ?? 'Untitled');

const excerpt = computed(() => {
  const contents = props.page?.contents ?? props.page?.page_contents;
  if (Array.isArray(contents)) {
    const textContent = contents.find((c) => (c.type ?? c.content_type) === 'text');
    const body = textContent?.content ?? textContent?.body ?? textContent?.text ?? '';
    if (body && typeof body === 'string') return body.trim();
  }
  const d = props.page?.description ?? props.page?.content ?? '';
  if (!d || typeof d !== 'string') return '—';
  return d.trim();
});

const imageUrl = computed(() => {
  const p = props.page;
  const media = p?.coverImage ?? p?.cover_image ?? p?.media;
  if (media?.url) return media.url;
  if (typeof p?.cover_image === 'string') return p.cover_image;
  if (typeof p?.image === 'string') return p.image;
  return '';
});

const readingTime = computed(() => props.page?.reading_time ?? props.page?.readingTime ?? '2m');
const viewCount = computed(() => props.page?.view_count ?? props.page?.viewCount ?? null);
const viewCountFormatted = computed(() => {
  const v = viewCount.value;
  if (v == null) return '';
  return v >= 1000 ? `${(v / 1000).toFixed(1)}k` : String(v);
});

const statusDotClass = computed(() => {
  const s = props.page?.status ?? props.page?.approval_status;
  if (s === 'PENDING' || s === 'pending') return 'bg-orange-300';
  return 'bg-gray-200';
});
</script>

<style scoped>
.page-card-enter-active {
  transition: opacity 0.24s cubic-bezier(0.32, 0.72, 0, 1), transform 0.24s cubic-bezier(0.32, 0.72, 0, 1);
}
.page-card-leave-active {
  transition: opacity 0.2s cubic-bezier(0.32, 0.72, 0, 1), transform 0.2s cubic-bezier(0.32, 0.72, 0, 1);
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 10;
  pointer-events: none;
}
.page-card-enter-from {
  opacity: 0;
  transform: translateY(10px);
}
.page-card-enter-to {
  opacity: 1;
  transform: translateY(0);
}
.page-card-leave-from {
  opacity: 1;
  transform: translateY(0);
}
.page-card-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}
</style>
